import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import {
	useContributionsPageState,
	useContributionsPageData,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';
import { convertToUsdNumber } from '#src/app/shared/utils/currency';

import { useButtonAmounts } from './hook/useButtonAmounts';
import { useCustomAmount } from './hook/useCustomAmount';

type ButtonAmount = {
	value: string;
	label: string;
};

type Result = ModelHookResult<
	{
		buttonAmounts: ButtonAmount[];
		selection: string;
		customAmount: string;
		amountErrorText: string | null;
		customAmountLabel: string;
		currencySymbol: string;
		currencySymbolPosition: 'before' | 'after';
	},
	{
		handleAmountChange: (value: string) => void;
		handleCustomAmountFocus: () => void;
		handleCustomAmountChange: (value: ChangeEvent<HTMLInputElement>) => void;
	}
>;

export function useMembershipAmountSelector(): Result {
	const { data } = useContributionsPageData();
	const [{ subscription: subs }, { setContributionAmount }] = useContributionsPageState();
	const { translate, getCurrencySymbol, getCurrencySymbolPosition } = useI18n();

	const buttonAmounts = useButtonAmounts();
	const customAmount = useCustomAmount();

	const derivedSelection = useMemo(
		() =>
			subs.config.amountChoices.some((amount) => amount === subs.unsavedAmount) ? `${subs.unsavedAmount}` : 'custom',
		[subs.config.amountChoices, subs.unsavedAmount],
	);

	const [selection, setSelection] = useState<string>(derivedSelection);
	const exchangeRate = data.currencyExchangeRate ?? 1;

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setSelection(derivedSelection);
	}, [derivedSelection]);

	const track = useTracking();

	const handleAmountChange = useCallback(
		(value: string): void => {
			setSelection(value);
			if (value !== 'custom') {
				const valueAsNumber = Number(value);
				setContributionAmount(valueAsNumber);
				void track('subs_settings_change_amount_click', {
					local_amount: valueAsNumber,
					amount: convertToUsdNumber(valueAsNumber, exchangeRate, subs.currencyCode),
				});
				void track('subs_settings_change_amount_changed', {
					local_amount: valueAsNumber,
					amount: convertToUsdNumber(valueAsNumber, exchangeRate, subs.currencyCode),
				});
			} else {
				customAmount.actions.handleCustomAmountSelect();
			}
		},
		[setContributionAmount, track, customAmount, exchangeRate, subs.currencyCode],
	);

	return {
		data: {
			buttonAmounts,
			selection,
			customAmount: customAmount.data.customAmount,
			amountErrorText: subs.validationErrors ? subs.validationErrors.amount : null,
			customAmountLabel: translate('fe.pages.sponsors.other_amount'),
			currencySymbol: getCurrencySymbol(subs.currencyCode),
			currencySymbolPosition: getCurrencySymbolPosition(subs.currencyCode),
		},
		actions: {
			handleAmountChange,
			handleCustomAmountFocus: customAmount.actions.handleCustomAmountFocus,
			handleCustomAmountChange: customAmount.actions.handleCustomAmountChange,
		},
	};
}
